import { ref } from 'vue'
import { useI18n } from 'vue-i18n'

import {
  MAX_FILE_SIZE_MB,
  NOT_ALLOWED_TYPES,
} from '@shared/constants/file-uploading-types'
import { FileSizeType } from '@shared/models/enums/file-size-type'
import { fileTypes } from '@shared/models/enums/files'
import { Nullable } from '@shared/models/types/primary'
import { compressImage } from '@shared/utils/helpers/compress-image'
import { createSharedComposable } from '@shared/utils/helpers/create-composable'
import { getFileTypeByName } from '@shared/utils/helpers/file/file-url'
import { fileSizeFormatter } from '@shared/utils/helpers/file-size-formatter'
import {
  fileNameHasNotAllowedTypes,
  fileTypeNotAllowed,
} from '@shared/utils/helpers/get-file-type'
import { isIosDevice } from '@shared/utils/helpers/is-mobile-device'

import { Attachment } from '@protos/chat/attachment_pb'

export interface AttachmentProps {
  maxSize?: Nullable<number>
  allowedTypes?: string[]
}

export const useChatInputActions = createSharedComposable(() => {
  const { t } = useI18n()
  const isInputFocused = ref(false)
  const isIphoneKeyboardOpened = ref(false)
  const inputRef = ref()
  const files = ref<File[]>([])
  const attachmentError = ref<string>()
  const fileInputRef = ref<HTMLInputElement>()
  const attachments = ref<
    Array<
      Pick<
        Attachment,
        | 'contentType'
        | 'fileName'
        | 'formSubmission'
        | 'form'
        | 'id'
        | 'quickSignDocument'
      > & {
        icon?: string
      }
    >
  >([])

  const removeFile = (idx: number) => {
    files.value.splice(idx, 1)
    return files.value
  }

  const focusInput = (shouldFocus: boolean) => {
    shouldFocus
      ? inputRef.value?.inputRef?.focus()
      : inputRef.value?.inputRef?.blur()
    isInputFocused.value = true
  }

  const setIsInputFocused = (value: boolean, callback?: () => void) => {
    /* eslint-disable-next-line no-console */
    isInputFocused.value = value

    // this logic need for scroll issue on IOS
    if (isIosDevice()) {
      const oldValue = isIphoneKeyboardOpened.value
      isIphoneKeyboardOpened.value = value
      if (oldValue && callback) {
        // callback is scrolToBottom  we need scroll only if isIphoneKeyboardOpened was true
        callback()
      }
    }
  }

  const selectFile = async (e: Event, options: AttachmentProps) => {
    attachmentError.value = ''
    const target = e.target as HTMLInputElement
    if (files.value?.length >= 1 || !target?.files?.[0]) {
      attachmentError.value =
        files.value?.length >= 1
          ? t('chat.labels.oneFilePerMessage')
          : t('common.noContent')
      return
    }

    let attachmentFile = target.files![0]

    const attachmentSizeInMegaBytes = fileSizeFormatter(
      attachmentFile?.size,
      FileSizeType.MB
    )
    const maxFileSize = options?.maxSize || MAX_FILE_SIZE_MB
    const isAcceptableSize = attachmentSizeInMegaBytes < maxFileSize
    const isAcceptableType =
      !options?.allowedTypes?.length ||
      options.allowedTypes?.find((type) => {
        return attachmentFile?.type.includes(type)
      })

    if (
      !isAcceptableSize ||
      !isAcceptableType ||
      fileNameHasNotAllowedTypes(NOT_ALLOWED_TYPES, attachmentFile.name) ||
      fileTypeNotAllowed(NOT_ALLOWED_TYPES, attachmentFile.type)
    ) {
      attachmentError.value = !isAcceptableSize
        ? t('chat.labels.attachmentFileLimit', {
            size: maxFileSize,
          })
        : t('chat.labels.attachmentFileTypeInvalid')
      return
    }

    if (attachmentFile.type.includes('image')) {
      attachmentFile = await compressImage(attachmentFile, 2, 1080)
    }

    addFile(attachmentFile)

    if (attachments.value.length) {
      attachments.value = []
    }

    if (fileInputRef.value) {
      fileInputRef.value.value = ''
    }
    return files.value
  }

  const resetAttachments = () => {
    files.value = []
    attachments.value = []
    attachmentError.value = ''
  }

  const addFile = (file?: File) => {
    if (file) {
      if (file?.name && fileTypes.GIF && getFileTypeByName(file.name)) {
        const type = getFileTypeByName(file.name)
        const correctedFile = new File([file], file.name, {
          type: type || file.type,
        })

        files.value.push(correctedFile)
      } else {
        files.value.push(file)
      }
    }
  }

  const drugFile = (event: DragEvent, options: AttachmentProps) => {
    event.preventDefault()
    if (event?.dataTransfer?.files && event?.dataTransfer?.files?.length) {
      const customEvent = {
        target: {
          files: event?.dataTransfer?.files,
        },
      } as unknown as Event
      selectFile(customEvent, options)
    }
  }

  return {
    focusInput,
    removeFile,
    selectFile,
    setIsInputFocused,
    resetAttachments,
    addFile,
    drugFile,

    attachmentError,
    isInputFocused,
    isIphoneKeyboardOpened,
    attachments,
    fileInputRef,
    inputRef,
    files,
  }
})
